import {
  BufferGeometry,
  ClampToEdgeWrapping,
  DataTexture,
  DataUtils,
  Float32BufferAttribute,
  HalfFloatType,
  LinearFilter,
  Mesh,
  MeshToonMaterial,
  PlaneGeometry,
  RedFormat,
  RGBAFormat,
  type Texture,
  UnsignedByteType,
  Vector2,
} from 'three';
import { createTerrainMaterial, NOISE_GLSL, OUTLINE, patchMrt, sharedUniforms } from '../materials/toon';
import type { Palette } from '../palette';

/** Lattice spacing of the height-field, metres. Half a metre keeps trench lips crisp. */
const STEP = 0.5;

export interface TerrainDef {
  /** South-west... strictly: the minimum x / minimum z corner, and the extent, in metres. */
  originX: number;
  originZ: number;
  sizeX: number;
  sizeZ: number;
  heightAt(x: number, z: number): number;
  /** Weights of the four ground materials at a point (need not be normalised). */
  materialAt(x: number, z: number): readonly [number, number, number, number];
  /** Palette ramp for each of the four materials, and the step each normally sits on. */
  ramps: readonly [string, string, string, string];
  baseSteps: readonly [number, number, number, number];
  /** Water surface height; omitted = no water. Water shows wherever the ground is below it. */
  waterLevel?: number;
  /** Rectangle the water surface covers, so trenches below the water level stay dry. */
  waterRegion?: { x: number; z: number; width: number; depth: number };
  /** Arctic: the water surface is ice with cracks, open only where it is deep. */
  frozen?: boolean;
}

export interface TerrainResult {
  ground: Mesh;
  water: Mesh | null;
}

export function buildTerrain(def: TerrainDef, palette: Palette, gradientMap: Texture): TerrainResult {
  const nx = Math.round(def.sizeX / STEP);
  const nz = Math.round(def.sizeZ / STEP);

  // --- height-field mesh (indexed, smooth normals so light bands follow the land's curves)
  const positions = new Float32Array((nx + 1) * (nz + 1) * 3);
  const heights = new Uint16Array((nx + 1) * (nz + 1));
  let v = 0;
  for (let j = 0; j <= nz; j++) {
    for (let i = 0; i <= nx; i++) {
      const x = def.originX + i * STEP;
      const z = def.originZ + j * STEP;
      const y = def.heightAt(x, z);
      positions[v * 3] = x;
      positions[v * 3 + 1] = y;
      positions[v * 3 + 2] = z;
      heights[v] = DataUtils.toHalfFloat(y);
      v++;
    }
  }
  const indices: number[] = [];
  for (let j = 0; j < nz; j++) {
    for (let i = 0; i < nx; i++) {
      const a = j * (nx + 1) + i;
      const b = a + 1;
      const c = a + (nx + 1);
      const d = c + 1;
      // Alternate the diagonal so long ridges don't show a directional grain.
      if ((i + j) % 2 === 0) indices.push(a, c, b, b, c, d);
      else indices.push(a, c, d, a, d, b);
    }
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();

  // --- splat map: which ground material wins where
  const splatData = new Uint8Array(nx * nz * 4);
  for (let j = 0; j < nz; j++) {
    for (let i = 0; i < nx; i++) {
      const w = def.materialAt(def.originX + (i + 0.5) * STEP, def.originZ + (j + 0.5) * STEP);
      const sum = Math.max(1e-6, w[0] + w[1] + w[2] + w[3]);
      const o = (j * nx + i) * 4;
      splatData[o] = Math.round((w[0] / sum) * 255);
      splatData[o + 1] = Math.round((w[1] / sum) * 255);
      splatData[o + 2] = Math.round((w[2] / sum) * 255);
      splatData[o + 3] = Math.round((w[3] / sum) * 255);
    }
  }
  const splat = new DataTexture(splatData, nx, nz, RGBAFormat, UnsignedByteType);
  splat.minFilter = LinearFilter;
  splat.magFilter = LinearFilter;
  splat.wrapS = ClampToEdgeWrapping;
  splat.wrapT = ClampToEdgeWrapping;
  splat.generateMipmaps = false;
  splat.needsUpdate = true;

  const mapOrigin = new Vector2(def.originX, def.originZ);
  const mapSize = new Vector2(def.sizeX, def.sizeZ);
  const ramps = def.ramps.flatMap((name) => [0, 1, 2, 3].map((s) => palette.color(`${name}.${s}`)));

  const ground = new Mesh(
    geometry,
    createTerrainMaterial({ gradientMap, splat, mapOrigin, mapSize, ramps, baseSteps: def.baseSteps }),
  );
  ground.receiveShadow = true;
  ground.castShadow = true;
  ground.name = 'terrain';

  let water: Mesh | null = null;
  if (def.waterLevel !== undefined) {
    const heightTex = new DataTexture(heights, nx + 1, nz + 1, RedFormat, HalfFloatType);
    heightTex.minFilter = LinearFilter;
    heightTex.magFilter = LinearFilter;
    heightTex.wrapS = ClampToEdgeWrapping;
    heightTex.wrapT = ClampToEdgeWrapping;
    heightTex.generateMipmaps = false;
    heightTex.needsUpdate = true;
    water = buildWater(def, palette, gradientMap, heightTex, def.waterLevel);
  }
  return { ground, water };
}

/**
 * Water / ice surface. Depth comes from the terrain height texture, not from scene depth,
 * so the look needs no extra pass: stepped body tones by depth, an animated foam line at
 * the shore, sparse sparkles; or, when frozen, ice with crack lines and dark open leads.
 */
function buildWater(
  def: TerrainDef,
  palette: Palette,
  gradientMap: Texture,
  heightTex: Texture,
  level: number,
): Mesh {
  const material = new MeshToonMaterial({ gradientMap });
  const tones = [0, 1, 2, 3].flatMap((s) => {
    const c = palette.color(`water.${s}`);
    return [c.r, c.g, c.b];
  });
  const snow = palette.color('ground.3');
  const frozen = def.frozen === true;

  material.onBeforeCompile = (shader) => {
    patchMrt(shader, OUTLINE.none);
    shader.uniforms['uHeight'] = { value: heightTex };
    shader.uniforms['uMapOrigin'] = { value: new Vector2(def.originX, def.originZ) };
    shader.uniforms['uMapSize'] = { value: new Vector2(def.sizeX, def.sizeZ) };
    shader.uniforms['uLevel'] = { value: level };
    shader.uniforms['uGrid'] = { value: new Vector2(def.sizeX / STEP, def.sizeZ / STEP) };
    shader.uniforms['uTones'] = { value: tones };
    shader.uniforms['uSnow'] = { value: [snow.r, snow.g, snow.b] };
    shader.uniforms['uFrozen'] = { value: frozen ? 1 : 0 };
    shader.uniforms['uCamTexel'] = sharedUniforms.uCamTexel;
    shader.uniforms['uTime'] = sharedUniforms.uTime;

    shader.vertexShader = shader.vertexShader
      .replace('void main() {', 'varying vec3 vWorldPos;\nvoid main() {')
      .replace(
        '#include <project_vertex>',
        '#include <project_vertex>\n  vWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;',
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        'void main() {',
        /* glsl */ `
varying vec3 vWorldPos;
uniform sampler2D uHeight;
uniform vec2 uMapOrigin;
uniform vec2 uMapSize;
uniform float uLevel;
uniform vec2 uGrid;
uniform vec3 uTones[4];
uniform vec3 uSnow;
uniform float uFrozen;
uniform vec2 uCamTexel;
uniform float uTime;
${NOISE_GLSL}
vec3 waterAlbedo() {
  // Height samples sit on lattice points, i.e. on texel centres of an (n+1)-wide texture.
  vec2 huv = ((vWorldPos.xz - uMapOrigin) / uMapSize * uGrid + 0.5) / (uGrid + 1.0);
  float ground = texture2D(uHeight, huv).r;
  float depth = uLevel - ground;
  vec2 cell = floor(gl_FragCoord.xy) + uCamTexel;
  float tick = floor(uTime * 5.0);

  if (uFrozen > 0.5) {
    // Ice sheet: pale, with snow dusting near the banks, crack lines, and open leads
    // (dark water) only over the deepest channel.
    float lead = valueNoise(vWorldPos.xz * 0.21 + 3.1);
    if (depth > 1.0 && lead > 0.66) return uTones[0];
    if (depth > 1.0 && lead > 0.63) return uTones[3];
    float crackA = abs(valueNoise(vWorldPos.xz * 0.55) - 0.5);
    float crackB = abs(valueNoise(vWorldPos.xz * 1.3 + 7.7) - 0.5);
    if (crackA < 0.012 || crackB < 0.008) return uTones[1];
    float dust = valueNoise(vWorldPos.xz * 0.4 + 11.0) + (hash12(cell) - 0.5) * 0.25;
    if (depth < 0.22 || dust > 0.74) return uSnow;
    return dust > 0.52 ? uTones[3] : uTones[2];
  }

  // Shore foam wobbles in steps so it animates like hand-drawn frames, not like a shader.
  float foamEdge = 0.10 + 0.06 * valueNoise(vWorldPos.xz * 1.1 + vec2(tick * 0.37, tick * 0.19));
  if (depth < foamEdge) return uTones[3];
  float sparkle = hash12(cell + tick * 13.0);
  if (sparkle > 0.9965) return uTones[3];
  float ripple = valueNoise(vec2(vWorldPos.x * 0.35, vWorldPos.z * 1.6 + tick * 0.21));
  if (depth < 0.42) return ripple > 0.62 ? uTones[3] : uTones[2];
  if (depth < 0.95) return ripple > 0.66 ? uTones[2] : uTones[1];
  return ripple > 0.70 ? uTones[1] : uTones[0];
}
void main() {`,
      )
      .replace('#include <color_fragment>', 'diffuseColor.rgb = waterAlbedo();');
  };
  material.customProgramCacheKey = () => `toon-mrt-water-${frozen ? 'ice' : 'open'}`;

  const region = def.waterRegion ?? {
    x: def.originX + def.sizeX / 2,
    z: def.originZ + def.sizeZ / 2,
    width: def.sizeX,
    depth: def.sizeZ,
  };
  const geometry = new PlaneGeometry(region.width, region.depth, 1, 1);
  geometry.rotateX(-Math.PI / 2);
  const mesh = new Mesh(geometry, material);
  mesh.position.set(region.x, level, region.z);
  mesh.receiveShadow = true;
  mesh.name = 'water';
  return mesh;
}
